import * as jsPDF from 'jspdf';
import * as _ from 'lodash';
import * as moment from 'moment';
import 'jspdf-autotable';
import { Helper } from '../helpers/helper';

export interface PdfConstructor {
  documentTitle: string;
  header: boolean;
  footer: boolean;
  orientation?: string;
  unit?: string;
  format?: any;
  compressPdf?: number;
}

export interface TitleColumn {
  text: string;
  xPosition: number;
  container?: boolean;
  containerBackgroungColor?: [number, number, number];
  fontSize?: number;
  fontStyle?: string;
  spacingBottom?: number;
}

export interface SubTitleColumn {
  text: string;
  xPosition: number;
  container?: boolean;
}

export interface TextWrap {
  fontSize?: number;
  lineSpacing: number;
  xPos?: number;
}

export interface ColumnData {
  steps: number[];
  labels: string[];
  values: any[];
  border?: boolean;
  backgroundColor?: [number, number, number];
  boldLabel?: boolean;
  boldValue?: boolean;
  disableMarginTop?: boolean;
}

export interface WriteText {
  text: string;
  fontSize?: number;
  fontStyle?: string;
  align?: string; // rigth, center, left, item, sub-item
  container?: boolean;
  containerBackgroungColor?: [number, number, number];
  line?: true;
  xPosition?: number;
  maxLength?: number;
}

export interface Table {
  headers: any[];
  body: any[];
  striped?: boolean;
}

export interface TextRow {
  label: string;
  value: string;
  fontSize?: number;
  xPosition?: number;
  boldLabel?: boolean;
  boldValue?: boolean;
}

export interface AssignatureCenterWithData {
  day?: string;
  month?: string;
  year?: string;
  city?: string;
}

export interface DocumentAssignatureColumn {
  label: string;
  lineSpacing: number;
  date?: boolean;
  xPosition?: number;
  assignatureWidth?: number;
  fontSize?: number;
  fontStyle?: string;
}

export interface DocumentDate {
  align?: string;
  city?: string;
}

export class Pdf extends jsPDF {
  // Document Variables
  private pdf: PdfConstructor;
  private companyId: number;
  private yIni = 10;
  private yIniWithHeader = 35;
  private yEnd = 265;
  private y = 10;
  private previousY = 0;
  private xIni = 10;
  private xEnd = 200;
  private document = new Document();

  // Text Wrap Variables
  private TextWrapCollection = [];

  private styles = {
    fonts: {
      title: { style: 'normal', size: 16 },
      subTitle: { style: 'normal', size: 12 },
      document: { style: 'normal', size: 8 },
    },
    rect: {
      default: { width: 190 },
      title: { height: 7 },
      subtitle: { height: 7 },
    }
  };

  constructor(pdf: PdfConstructor) {
    super(pdf.orientation, pdf.unit, pdf.format, pdf.compressPdf);
    this.setProperties({ title: pdf.documentTitle });
    this.pdf = pdf;

    this.setDrawColor(180, 180, 180);

    if (pdf.header) {
      this.documentHeader();
      this.setY(this.yIniWithHeader);
    }

    if (pdf.footer) {
      this.documentFooter();
    }
  }

  public addY = (increment: number) => {
    const nextY = this.y + increment;
    if (nextY >= this.yEnd) {
      this.addPage();

      this.pdf.header === true ? this.setY(this.yIniWithHeader) : this.setY(this.yIni);

      if (this.pdf.header) {
        this.documentHeader();
      }

      if (this.pdf.footer) {
        this.documentFooter();
      }

    } else {
      this.y += increment;
    }
  }

  public getY = (): number => this.y;

  public setY = (value: number) => this.y = value;

  public showHeader = (value: boolean) => this.pdf.header = value;

  public showFooter = (value: boolean) => this.pdf.footer = value;

  public addNewPage = () => this.addY(500);

  public pullLine = (height?: number) => {
    if (height !== undefined) {
      this.addY(height);
    } else {
      this.addY(10);
    }
  }

  public resetFont = () => (this.setFontStyle(this.styles.fonts.document.style), this.setFontSize(this.styles.fonts.document.size));

  public addTextToWrap = (text: string) => {
    text.split(' ')
      .map((textSeparated: string) => this.TextWrapCollection.push(textSeparated.trim()));
  }

  public documentHeader = (): void => {
    const logo = Helper.documentDefaultLogo();
    this.addImage(logo, '', 10, 10, 55, 15);
  }

  public documentFooter = (): void => {
    /* const imageBar = Helper.barFooter();
    this.addImage(imageBar, '', 15, 274, 5, 15); */

    this.setFontSize(10);
    this.setTextColor(155, 153, 159);
    this.text('BR 101 Norte - Km 38, Distrito Industrial - Itapissuma - PE, 53700-000, Brasil', 25, 280);

    // tslint:disable-next-line: max-line-length
    const footerSiteText = ``;

    const pageWidth = this.internal.pageSize.width;
    const xPosText = pageWidth / 2;
    this.text(footerSiteText, xPosText, 285, null, null, 'center');

    this.setTextColor(0, 0, 0);
  }

  public addDataColumn = (data: ColumnData): void => {
    const columnHeight = 8;

    if (data.disableMarginTop === false || data.disableMarginTop === undefined) {
      if (this.getY() + columnHeight > this.yEnd) {
        this.addNewPage();
      } else {
        this.addY(columnHeight);
      }
    }

    if (data.border === true && data.backgroundColor !== undefined) {
      this.setFillColor(data.backgroundColor[0], data.backgroundColor[1], data.backgroundColor[2]);
      this.rect(this.xIni, this.y, this.styles.rect.default.width, columnHeight, 'FD');
      this.drawDataColumnLines(data.steps, this.y, (this.y + columnHeight));
    } else if (data.border === true) {
      this.rect(this.xIni, this.y, this.styles.rect.default.width, columnHeight);
      this.drawDataColumnLines(data.steps, this.y, (this.y + columnHeight));
    }

    for (const [index, step] of data.steps.entries()) {
      this.resetFont();
      this.setFontSize(7.5);

      if (data.boldLabel === true) {
        this.setFontStyle('bold');
      }

      this.text((data.labels[index]) ? data.labels[index].toString() : '', (step + 2), (this.y + 3.5));

      this.resetFont();
      this.setFontSize(8.5);

      if (data.boldValue === true) {
        this.setFontStyle('bold');
      }

      this.text((data.values[index]) ? data.values[index].toString() : '', (step + 4), (this.y + 7));
    }

    this.resetFont();
  }

  private drawDataColumnLines = (steps: number[], lineStart: number, lineEnd: number) => {
    for (const step of steps) {
      this.line(step, lineStart, step, lineEnd);
    }
  }

  public wrapText = (data: TextWrap): void => {
    const textSentences = [];
    let textProcessed = '';

    (data.fontSize !== undefined) ? this.setFontSize(data.fontSize) : this.resetFont();

    for (const textToProcess of this.TextWrapCollection) {
      const textProcessedWidth = this.getStringUnitWidth(textProcessed) * this.internal.getFontSize() / this.internal.scaleFactor;
      const textToProcessWidth = this.getStringUnitWidth(textToProcess) * this.internal.getFontSize() / this.internal.scaleFactor;

      if ((textProcessedWidth + textToProcessWidth) >= 180) {
        textSentences.push(textProcessed);
        textProcessed = `${textToProcess}`;
      } else {
        textProcessed = `${textProcessed} ${textToProcess}`;
      }
    }

    // Inserindo qualquer texto remanescente da tratativa de dados;
    textSentences.push(textProcessed);

    const xPosition = data.xPos ? data.xPos : 10;

    for (const [index, sentence] of textSentences.entries()) {
      if (index > 0) {
        this.addY(data.lineSpacing);
      }

      (data.fontSize !== undefined) ? this.setFontSize(data.fontSize) : this.resetFont();

      this.text(sentence.toString().trim(), xPosition, this.y, { align: 'justify', maxWidth: 190 });
    }

    this.TextWrapCollection = [];
  }

  public writeClauseTitle = (title: string): void => {
    this.writeText({
      text: title,
      align: 'center',
      fontSize: 11,
      fontStyle: 'bold',
      container: true,
      containerBackgroungColor: [200, 200, 200]
    });
  }

  public writeJustifyText = (obj: { text?: string }): void => {
    this.writeText({
      text: obj.text,
      align: 'justify',
      fontSize: 11
    });
  }

  public writeClauseItem = (text: string): void => {
    this.writeText({
      text: `${text}`,
      align: 'justify',
      xPosition: 20,
      maxLength: 180,
      fontSize: 11
    });
  }

  public writeText = (data: WriteText): void => {
    this.resetFont();
    this.setFont('times');

    if (data.fontSize !== undefined) {
      this.setFontSize(data.fontSize);
    }

    if (data.fontStyle !== undefined) {
      this.setFontStyle(data.fontStyle);
    }

    const fontSize = this.internal.getFontSize();
    const pageWidth = this.internal.pageSize.width;
    const textWidth = this.getStringUnitWidth(data.text) * fontSize / this.internal.scaleFactor;
    const textHeight = this.getTextDimensions(data.text).h;
    const maxLength = data.maxLength ? data.maxLength : 190;
    const xPosition = data.xPosition ? data.xPosition : 10;

    if (data.container !== undefined && data.container === true && data.containerBackgroungColor !== undefined) {
      this.setFillColor(data.containerBackgroungColor[0], data.containerBackgroungColor[1], data.containerBackgroungColor[2]);
      this.rect(xPosition, (this.y - (textHeight + 0.6)), maxLength + 1, (textHeight + 2), 'FD');
    } else if (data.container !== undefined && data.container === true) {
      this.rect(xPosition, (this.y - textHeight), maxLength, (textHeight + 2));
    }

    let xPosText: number;

    switch (data.align) {
      case undefined:
        if (data.xPosition !== undefined) {
          xPosText = data.xPosition;
        } else {
          xPosText = (data.container === true) ? 12 : 10;
        }
        (data.container === true) ? this.text(data.text, xPosText, this.y) : this.text(data.text, xPosText, this.y);
        break;
      case 'center':
        xPosText = pageWidth / 2;
        this.text(data.text, xPosText, this.y, null, null, 'center');
        break;
      case 'right':
        xPosText = (data.container === true) ? this.xEnd - (textWidth + 2) : this.xEnd - textWidth;
        this.text(data.text, xPosText, this.y);
        break;
      case 'left':
        xPosText = (data.container === true) ? 12 : 10;
        (data.container === true) ? this.text(data.text, xPosText, this.y) : this.text(data.text, xPosText, this.y);
        break;
      case 'item':
        this.text(data.text, 20, this.y);
        break;
      case 'sub-item':
        this.text(data.text, xPosition + 2, this.y);
        break;
      case 'justify':
        this.text(data.text, xPosition, this.y, { maxWidth: maxLength, align: 'justify' });
        break;
      default:
        this.text(data.text, xPosition, this.y);
        break;
    }

    if (data.line !== undefined && data.line === true) {
      this.addY(3);
      this.line(10, this.y, 200, this.y);
    }

    this.resetFont();
  }

  public addTable = (data: Table): void => {
    let tableHeight = 0;

    const stripedTable = () => {
      const styles: { fillColor?: number[] } = {};

      if (data.striped !== undefined && data.striped === true) {
        styles.fillColor = [211, 211, 211];
      }

      return styles;
    };

    ((this as any).autoTable({
      head: data.headers,
      body: data.body,
      startY: this.y,
      theme: 'grid',
      margin: {
        bottom: 20
      },
      headStyles: {
        fillColor: [45, 52, 54],
      },
      alternateRowStyles: stripedTable(),
      didDrawCell: (settings: any) => {
        tableHeight = settings.table.height;
      }
    }));

    if (this.pdf.footer) {
      this.documentFooter();
    }

    this.addY(tableHeight);
  }

  public textRow = (data: TextRow): void => {
    let xPos: number;

    this.resetFont();

    if (data.fontSize !== undefined) {
      this.setFontSize(data.fontSize);
    }

    if (data.boldLabel !== undefined && data.boldLabel === true) {
      this.setFontStyle('bold');
    }

    (data.xPosition !== undefined) ? xPos = data.xPosition : xPos = this.xIni;


    this.text(`${data.label}:`, xPos, this.y);

    const textWidth = this.getStringUnitWidth(`${data.label}: `) * this.internal.getFontSize() / this.internal.scaleFactor;

    xPos += textWidth;

    if (data.boldValue !== undefined && data.boldValue === true) {
      this.setFontStyle('bold');
    } else {
      this.setFontStyle('normal');
    }

    this.text(data.value, xPos, this.y);
  }

  public date = (options?: DocumentDate): void => {
    const defaultOptions = {
      city: 'Jaboatão dos Guararapes',
      align: 'right'
    };

    const date = new Date();
    const monthName = Helper.getMonthName(String((date.getMonth() + Number(1))).padStart(2, '0'));
    const city = (!_.isUndefined(options) && !_.isUndefined(options.city)) ? options.city : defaultOptions.city;
    const align = (!_.isUndefined(options) && !_.isUndefined(options.align)) ? options.align : defaultOptions.align;

    const text = `${city}, ${date.getDate()} de ${monthName} de ${date.getFullYear()}`;

    this.setFontSize(10);

    let xPos: number;

    const fontSize = this.internal.getFontSize();
    const pageWidth = this.internal.pageSize.width;
    const textWidth = this.getStringUnitWidth(text) * fontSize / this.internal.scaleFactor;

    if (align === 'left') {
      xPos = 10;
    } else if (align === 'center') {
      xPos = pageWidth / 2;
    } else if (align === 'right') {
      xPos = this.xEnd - (textWidth + 5);
    } else {
      xPos = 10;
    }

    this.setTextColor(0, 58, 107);

    if (align === 'center') {
      this.text(text, xPos, this.getY(), null, null, align);
    } else {
      this.text(text, xPos, this.getY());
    }

    this.setTextColor(0, 0, 0);
  }


  public assignatureCenterWithData = (data: AssignatureCenterWithData) => {
    let day: string;
    let month: string;
    let year: string;
    let city: string;

    if (_.isEmpty(data) || Object.keys(data).length === 1 && Object.keys(data)[0] === 'city') {
      day = moment().format('DD');
      month = Helper.getMonthName(moment().format('MM'));
      year = moment().format('YYYY');
    } else {
      day = data.day;
      month = Helper.getMonthName(data.month);
      year = data.year;
    }

    if (data.city !== undefined) {
      city = data.city.concat(',');
    } else {
      city = '';
    }

    this.resetFont();
    this.setFontSize(10);

    const xPosition = this.internal.pageSize.width / 2;

    this.setTextColor(180, 180, 180);
    this.text('____________________________________________________', xPosition, this.y, null, null, 'center');

    this.addY(5);
    this.setTextColor(0, 0, 0);
    this.text(`${city} ${day} de ${month} de ${year} `, xPosition, this.y, null, null, 'center');
  }

  public documentDateCenter = (y: number, cityName?: string): void => {
    const date = new Date();
    const day = date.getDate();
    let monthId: any = date.getMonth() + Number(1);

    if (monthId < 10) {
      monthId = `0${monthId} `;
    }
    const month = Helper.getMonthName(monthId);
    const year = date.getFullYear();

    let city = '';

    if (cityName !== undefined) {
      if (cityName.trim().length > 0) {
        city = `${cityName}, `;
      }
    }

    this.setFontSize(10);
    this.text(`${city} ${day} de ${month} de ${year} `, 100, y, { align: 'center' });
  }

  public documentAssignatureColumn = (data: DocumentAssignatureColumn) => {
    const initialY = this.y;
    let xPos: number;
    let assignatureWidth: number;

    if (data.assignatureWidth !== undefined) {
      assignatureWidth = data.assignatureWidth;
    } else {
      assignatureWidth = 30;
    }

    this.resetFont();

    if (data.fontSize !== undefined) {
      this.setFontSize(data.fontSize);
    }

    if (data.fontStyle !== undefined) {
      this.setFontStyle(data.fontStyle);
    }

    if (data.xPosition !== undefined && typeof (data.xPosition) === 'number') {
      xPos = data.xPosition;
    } else {
      xPos = this.xIni;
    }

    this.text(data.label, xPos, this.y);

    if (data.date !== undefined && data.date === true) {
      this.addY(data.lineSpacing);
      this.text('Data: ____/____/______', xPos, this.y);
    }

    let assignatureLine = '';

    for (let i = 1; i <= assignatureWidth; i++) {
      assignatureLine = `${assignatureLine} _`;
    }

    this.addY(data.lineSpacing);
    this.text(`Assinatura: ${assignatureLine} `, xPos, this.y);

    this.setY(initialY);
  }

  public documentSignature = (y: number, signatureName: string): void => {
    this.setLineWidth(0.1);
    this.line(50, y, 155, y);
    this.setFontSize(10);
    this.text(signatureName, 100, (y + 5), { align: 'center' });
  }

  public documentTwoWitnessSignature = (y: number, firstAssignatureText: string, secondAssignatureText: string): void => {
    this.line(30, y, 100, y);
    this.line(110, y, 180, y);
    this.setFontSize(10);
    this.text(firstAssignatureText, 50, y + 5);
    this.text(secondAssignatureText, 130, y + 5);
  }

  public createTableHeaders = (keys) => {
    const result = [];
    // tslint:disable-next-line: prefer-for-of
    for (let i = 0; i < keys.length; i += 1) {
      result.push({
        id: keys[i],
        name: keys[i],
        prompt: keys[i],
        width: 70,
        height: 1000,
        align: 'center',
        padding: 0
      });
    }

    return result;
  }

  public createTableData = (data) => {
    const result = [];
    data.map(object => result.push(Object.assign({}, object)));
    return result;
  }

}
